/* @ts-self-types="./bs.d.ts" */

/**
 * Bits in `Bullets.flags` and `Lasers.flags`, for example `flags[i] & Flag.CanHit`.
 * @enum {1 | 2 | 4 | 8}
 */
export const Flag = Object.freeze({
    /**
     * Spawned by the host, with `spawn` or `eval`.
     */
    Host: 1, "1": "Host",
    /**
     * Its script is still running.
     */
    Scripted: 2, "2": "Scripted",
    /**
     * It can hit the player.
     */
    CanHit: 4, "4": "CanHit",
    /**
     * A cancel can remove it.
     */
    Cancellable: 8, "8": "Cancellable",
});

/**
 * What `Lasers.phase` holds.
 * @enum {0 | 1 | 2}
 */
export const LaserPhase = Object.freeze({
    /**
     * The warning line: it can't hit.
     */
    Warn: 0, "0": "Warn",
    /**
     * The beam.
     */
    Active: 1, "1": "Active",
    /**
     * Fading out: it can't hit.
     */
    Fade: 2, "2": "Fade",
});

export class Vm {
    static __wrap(ptr) {
        const obj = Object.create(Vm.prototype);
        obj.__wbg_ptr = ptr;
        VmFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VmFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vm_free(ptr, 0);
    }
    /**
     * The beat clock, or null when it's off.
     * @returns {Beat | null}
     */
    beatClock() {
        const ret = wasm.vm_beatClock(this.__wbg_ptr);
        return ret;
    }
    /**
     * The live bullets and enemies, as views into the VM's memory. See `Bullets` for how long
     * they stay good.
     * @returns {Bullets}
     */
    bullets() {
        const ret = wasm.vm_bullets(this.__wbg_ptr);
        return ret;
    }
    /**
     * Cancels the cancellable fired actors carrying any of `tags`, or every one for `[]`.
     * Returns how many went.
     * @param {string[]} tags
     * @returns {number}
     */
    cancel(tags) {
        const ptr0 = passArrayJsValueToWasm0(tags, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vm_cancel(this.__wbg_ptr, ptr0, len0);
        return ret >>> 0;
    }
    clearBeat() {
        wasm.vm_clearBeat(this.__wbg_ptr);
    }
    clearBounds() {
        wasm.vm_clearBounds(this.__wbg_ptr);
    }
    /**
     * Empties the event queue and resets the dropped count. Call it once the events are
     * handled, usually after each tick.
     */
    clearEvents() {
        wasm.vm_clearEvents(this.__wbg_ptr);
    }
    /**
     * @param {string} name
     */
    clearInput(name) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vm_clearInput(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * No targets: `(aim)` points straight down.
     */
    clearTargets() {
        wasm.vm_clearTargets(this.__wbg_ptr);
    }
    clear() {
        wasm.vm_clear(this.__wbg_ptr);
    }
    /**
     * Runs `src` at (x, y): definitions, or an expression or script, which runs at once until
     * its first wait or its end. See `EvalResult` for what a failure leaves behind.
     * @param {string} src
     * @param {number} x
     * @param {number} y
     * @returns {EvalResult}
     */
    eval(src, x, y) {
        const ptr0 = passStringToWasm0(src, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vm_eval(this.__wbg_ptr, ptr0, len0, x, y);
        return ret;
    }
    /**
     * How many events were dropped since the last `clearEvents()`.
     * @returns {number}
     */
    eventsDropped() {
        const ret = wasm.vm_eventsDropped(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The queued events, oldest first: everything since the last `clearEvents()`. Reading
     * them doesn't clear them. At most `maxEvents` of them; when more pile up, the oldest go
     * and `eventsDropped()` counts them.
     * @returns {VmEvent[]}
     */
    events() {
        const ret = wasm.vm_events(this.__wbg_ptr);
        return ret;
    }
    /**
     * Removes an actor, with a despawned event. Returns false if the id is stale.
     * @param {number} id
     * @returns {boolean}
     */
    kill(id) {
        const ret = wasm.vm_kill(this.__wbg_ptr, id);
        return ret !== 0;
    }
    /**
     * @param {number} kind
     * @returns {string}
     */
    kindName(kind) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.vm_kindName(this.__wbg_ptr, kind);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The live lasers, as views like `bullets`.
     * @returns {Lasers}
     */
    lasers() {
        const ret = wasm.vm_lasers(this.__wbg_ptr);
        return ret;
    }
    /**
     * The limits this VM runs under. A restore takes them from the snapshot, except
     * `maxEvents`, which stays this VM's.
     * @returns {Limits}
     */
    limits() {
        const ret = wasm.vm_limits(this.__wbg_ptr);
        return ret;
    }
    /**
     * Adds definitions, or replaces them: running scripts pick up the new ones on their next
     * call. A load that fails changes nothing.
     * @param {string} src
     * @returns {LoadResult}
     */
    load(src) {
        const ptr0 = passStringToWasm0(src, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vm_load(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * Every function defined, with how many positional arguments it takes.
     * @returns {{ name: string; arity: number }[]}
     */
    names() {
        const ret = wasm.vm_names(this.__wbg_ptr);
        return ret;
    }
    /**
     * A VM with the standard library loaded. The seed drives every random draw: a whole number
     * from 0 to 2^53 - 1, or a bigint for any 64-bit seed. Anything else throws, as do limits
     * that aren't a full `Limits`. Leave `limits` out for `defaultLimits()`.
     * @param {number | bigint} seed
     * @param {Limits} [limits]
     */
    constructor(seed, limits) {
        const ret = wasm.vm_new(seed, limits);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        VmFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Replaces the simulation with bytes from `snapshotBytes`, seed and limits included, and
     * clears the events. On failure the VM is unchanged.
     * @param {Uint8Array} bytes
     * @returns {RestoreResult}
     */
    restoreBytes(bytes) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vm_restoreBytes(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * The seed every random draw comes from, as a bigint. A restore takes it from the
     * snapshot.
     * @returns {bigint}
     */
    seed() {
        const ret = wasm.vm_seed(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Starts the beat clock: `bpm` beats a minute, with beat 0 at tick `offsetTicks`. A tempo
     * that isn't a positive finite number, or an offset that isn't finite, clears it.
     * @param {number} bpm
     * @param {number} offsetTicks
     */
    setBeat(bpm, offsetTicks) {
        wasm.vm_setBeat(this.__wbg_ptr, bpm, offsetTicks);
    }
    /**
     * Fired actors outside this rectangle are removed at the end of each tick. Actors the host
     * spawned stay wherever they go.
     * @param {number} minX
     * @param {number} minY
     * @param {number} maxX
     * @param {number} maxY
     */
    setBounds(minX, minY, maxX, maxY) {
        wasm.vm_setBounds(this.__wbg_ptr, minX, minY, maxX, maxY);
    }
    /**
     * @param {string} name
     * @param {number} value
     */
    setInput(name, value) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vm_setInput(this.__wbg_ptr, ptr0, len0, value);
    }
    /**
     * Moves an actor. Returns false if the id is stale.
     * @param {number} id
     * @param {number} x
     * @param {number} y
     * @returns {boolean}
     */
    setPosition(id, x, y) {
        const ret = wasm.vm_setPosition(this.__wbg_ptr, id, x, y);
        return ret !== 0;
    }
    /**
     * Whether spawning queues "spawned" events. Off by default.
     * @param {boolean} on
     */
    setSpawnEvents(on) {
        wasm.vm_setSpawnEvents(this.__wbg_ptr, on);
    }
    /**
     * Aims at one target until it's changed.
     * @param {number} x
     * @param {number} y
     * @param {number} vx
     * @param {number} vy
     */
    setTarget(x, y, vx, vy) {
        wasm.vm_setTarget(this.__wbg_ptr, x, y, vx, vy);
    }
    /**
     * Aims at several targets: x, y, vx, vy for each. Throws, changing nothing, if the length
     * isn't a multiple of 4.
     * @param {Float64Array} data
     */
    setTargets(data) {
        const ptr0 = passArrayF64ToWasm0(data, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vm_setTargets(this.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * The whole simulation as bytes, for saving or sending. It restores into any VM with the
     * same program loaded, on any platform.
     * @returns {Uint8Array}
     */
    snapshotBytes() {
        const ret = wasm.vm_snapshotBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * An independent copy of the whole VM, with an empty event queue.
     * @returns {Vm}
     */
    snapshot() {
        const ret = wasm.vm_snapshot(this.__wbg_ptr);
        return Vm.__wrap(ret);
    }
    /**
     * Starts the function `name` at (x, y) with `args`, which must match its parameters. The
     * kind defaults to `emitter`.
     * @param {string} name
     * @param {number} x
     * @param {number} y
     * @param {Float64Array} args
     * @param {string | null} [kind]
     * @returns {SpawnResult}
     */
    spawn(name, x, y, args, kind) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayF64ToWasm0(args, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(kind) ? 0 : passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.vm_spawn(this.__wbg_ptr, ptr0, len0, x, y, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @returns {string}
     */
    stateHash() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.vm_stateHash(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The tag names: bit i of a tag mask is name i.
     * @returns {string[]}
     */
    tagNames() {
        const ret = wasm.vm_tagNames(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]);
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {number}
     */
    tickCount() {
        const ret = wasm.vm_tickCount(this.__wbg_ptr);
        return ret;
    }
    /**
     * Runs one tick with the current targets and inputs.
     */
    tick() {
        wasm.vm_tick(this.__wbg_ptr);
    }
}
if (Symbol.dispose) Vm.prototype[Symbol.dispose] = Vm.prototype.free;

/**
 * The limits `new Vm(seed)` uses.
 * @returns {Limits}
 */
export function defaultLimits() {
    const ret = wasm.defaultLimits();
    return ret;
}

/**
 * Formats source the way the examples are written, breaking lines longer than `width` where
 * it can, or gives the reader's error the way `load` reports errors.
 * @param {string} src
 * @param {number} width
 * @returns {FormatResult}
 */
export function format(src, width) {
    const ptr0 = passStringToWasm0(src, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.format(ptr0, len0, width);
    return ret;
}

/**
 * The simulation version: it goes up whenever the same source, seed and inputs could give
 * different results.
 * @returns {number}
 */
export function simVersion() {
    const ret = wasm.simVersion();
    return ret >>> 0;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_30c8987f7c2ed4e2: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg___wbindgen_bigint_get_as_i64_a2383202b9353e4c: function(arg0, arg1) {
            const v = arg1;
            const ret = typeof(v) === 'bigint' ? v : undefined;
            getDataViewMemory0().setBigInt64(arg0 + 8 * 1, isLikeNone(ret) ? BigInt(0) : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_debug_string_4687d8d8c2017d52: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_is_undefined_8865fb403f8fe9d8: function(arg0) {
            const ret = arg0 === undefined;
            return ret;
        },
        __wbg___wbindgen_jsval_eq_02babf21faa37971: function(arg0, arg1) {
            const ret = arg0 === arg1;
            return ret;
        },
        __wbg___wbindgen_number_get_2e0e7dee9f701a71: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'number' ? obj : undefined;
            getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_string_get_0380ccaa2f57f0d9: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'string' ? obj : undefined;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_throw_41e9ee4f547fc59a: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg_get_31af05bd4842a84f: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_get_6c896e0571ddae51: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_instanceof_Object_67a83cdc00c5d141: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Object;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_keys_440be172f17c0265: function(arg0) {
            const ret = Object.keys(arg0);
            return ret;
        },
        __wbg_length_d4bdea10311bd9cf: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_new_617a8cdb8bb1130e: function() {
            const ret = new Object();
            return ret;
        },
        __wbg_parse_0fc53dead14b3b42: function() { return handleError(function (arg0, arg1) {
            const ret = JSON.parse(getStringFromWasm0(arg0, arg1));
            return ret;
        }, arguments); },
        __wbg_set_145a351398b48c65: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = Reflect.set(arg0, arg1, arg2);
            return ret;
        }, arguments); },
        __wbindgen_generic_0000000000000001: function(arg0) {
            // Cast intrinsic for `F64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_generic_0000000000000002: function(arg0, arg1) {
            // Cast intrinsic for `Ref(Slice(F32)) -> NamedExternref("Float32Array")`.
            const ret = getArrayF32FromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_generic_0000000000000003: function(arg0, arg1) {
            // Cast intrinsic for `Ref(Slice(U32)) -> NamedExternref("Uint32Array")`.
            const ret = getArrayU32FromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_generic_0000000000000004: function(arg0, arg1) {
            // Cast intrinsic for `Ref(Slice(U64)) -> NamedExternref("BigUint64Array")`.
            const ret = getArrayU64FromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_generic_0000000000000005: function(arg0, arg1) {
            // Cast intrinsic for `Ref(Slice(U8)) -> NamedExternref("Uint8Array")`.
            const ret = getArrayU8FromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_generic_0000000000000006: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_generic_0000000000000007: function(arg0) {
            // Cast intrinsic for `U64 -> Externref`.
            const ret = BigInt.asUintN(64, arg0);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./bs_bg.js": import0,
    };
}

const VmFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vm_free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayF32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayJsValueFromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    const mem = getDataViewMemory0();
    const result = [];
    for (let i = ptr; i < ptr + 4 * len; i += 4) {
        result.push(wasm.__wbindgen_externrefs.get(mem.getUint32(i, true)));
    }
    wasm.__externref_drop_slice(ptr, len);
    return result;
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getBigUint64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedBigUint64ArrayMemory0 = null;
function getBigUint64ArrayMemory0() {
    if (cachedBigUint64ArrayMemory0 === null || cachedBigUint64ArrayMemory0.byteLength === 0) {
        cachedBigUint64ArrayMemory0 = new BigUint64Array(wasm.memory.buffer);
    }
    return cachedBigUint64ArrayMemory0;
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayJsValueToWasm0(array, malloc) {
    const ptr = malloc(array.length * 4, 4) >>> 0;
    for (let i = 0; i < array.length; i++) {
        const add = addToExternrefTable0(array[i]);
        getDataViewMemory0().setUint32(ptr + 4 * i, add, true);
    }
    WASM_VECTOR_LEN = array.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedBigUint64ArrayMemory0 = null;
    cachedDataViewMemory0 = null;
    cachedFloat32ArrayMemory0 = null;
    cachedFloat64ArrayMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (!module.ok) {
            throw new Error(`failed to fetch Wasm: ${module.status} ${module.statusText} fetching '${module.url}'`);
        }

        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('bs_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
